import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ArrowRight, Users, Bus, Compass, Car, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BUCKETS = [
  { key: 'transfer',          label: 'Transferts',      icon: Car,     color: 'text-blue-400',    bg: 'bg-blue-500/10',    border: 'border-blue-500/20' },
  { key: 'excursion.private', label: 'Privées',         icon: Compass, color: 'text-purple-400',  bg: 'bg-purple-500/10',  border: 'border-purple-500/20' },
  { key: 'excursion.shared',  label: 'Partagées',       icon: Users,   color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  { key: 'excursion.shuttle', label: 'Navettes',        icon: Bus,     color: 'text-orange-400',  bg: 'bg-orange-500/10',  border: 'border-orange-500/20' },
  { key: 'excursion.return',  label: 'Retours',         icon: RotateCcw, color: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
];

export default function DispatchKpiByType({ date, dateEnd, getHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchKpi = useCallback(async () => {
    if (!getHeaders) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      if (dateEnd) params.set('date_end', dateEnd);
      const { data: res } = await axios.get(
        `${API}/dispatch/pro/kpi/by-type?${params.toString()}`,
        { headers: getHeaders() }
      );
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [date, dateEnd, getHeaders]);

  useEffect(() => {
    fetchKpi();
    const t = setInterval(fetchKpi, 30000); // refresh every 30s
    return () => clearInterval(t);
  }, [fetchKpi]);

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-[10px] text-white/30 px-2" data-testid="kpi-by-type-loading">
        {loading ? 'Chargement KPI…' : 'KPI indisponible'}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 overflow-x-auto" data-testid="kpi-by-type-widget">
      {BUCKETS.map(({ key, label, icon: Icon, color, bg, border }) => {
        const b = data.by_type?.[key] || { total: 0, unassigned: 0, in_progress: 0, completed: 0 };
        return (
          <div
            key={key}
            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${bg} ${border} min-w-[120px]`}
            data-testid={`kpi-bucket-${key}`}
          >
            <Icon className={`w-3.5 h-3.5 ${color}`} />
            <div className="flex flex-col leading-tight">
              <span className="text-[9px] text-white/40 uppercase tracking-wide">{label}</span>
              <span className={`text-sm font-bold ${color}`}>{b.total}</span>
              {b.total > 0 && (
                <span className="text-[9px] text-white/40">
                  {b.unassigned > 0 && (
                    <span className="text-amber-400">{b.unassigned} n/a</span>
                  )}
                  {b.unassigned > 0 && b.in_progress > 0 && ' · '}
                  {b.in_progress > 0 && (
                    <span className="text-cyan-400">{b.in_progress} actif</span>
                  )}
                </span>
              )}
            </div>
          </div>
        );
      })}
      {data.totals?.return_unassigned_pct > 0 && data.by_type?.['excursion.return']?.total > 0 && (
        <div className="flex items-center gap-1 px-2 py-1 rounded border border-red-500/30 bg-red-500/10" data-testid="kpi-alert-returns">
          <ArrowRight className="w-3 h-3 text-red-400" />
          <span className="text-[10px] text-red-300">
            {data.totals.return_unassigned_pct}% retours à assigner
          </span>
        </div>
      )}
    </div>
  );
}
